import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  ClipboardText, ShieldCheck, CheckCircle, ArrowRight,
  Phone, Calendar, Users, Warning, FileText, MagnifyingGlass,
  Lightbulb, ChartLineUp, Star, Medal
} from "@phosphor-icons/react";

const HospiceSurveysPage = () => {
  const surveyAreas = [
    {
      title: "Patient Rights",
      items: ["Notice of Rights", "Informed Consent", "Advance Directives", "Privacy & Confidentiality"]
    },
    {
      title: "Comprehensive Assessment",
      items: ["Initial Assessment", "Updated Assessments", "Interdisciplinary Input", "Patient/Family Goals"]
    },
    {
      title: "Plan of Care",
      items: ["Individualized Care Plans", "Measurable Goals", "15-Day Reviews", "IDG Updates"]
    },
    {
      title: "Quality Assessment (QAPI)",
      items: ["Quality Indicators", "Performance Improvement", "Data Collection", "Outcome Tracking"]
    }
  ];

  const commonDeficiencies = [
    "Incomplete comprehensive assessments",
    "Plans of care not individualized",
    "Missing or late IDG reviews",
    "Inadequate QAPI documentation",
    "Volunteer program deficiencies",
    "Staff competency gaps",
    "Medication management issues",
    "Coordination of care problems"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Hospice Survey Preparation | CMS Survey Readiness | Noble Hospice Solutions</title>
        <meta name="description" content="Expert hospice survey preparation services. We help hospice agencies achieve zero-deficiency surveys through comprehensive readiness assessments, mock surveys, and staff training. Free consultation." />
        <meta name="keywords" content="hospice survey, CMS hospice survey, hospice survey preparation, hospice survey deficiencies, hospice compliance survey, state hospice survey, hospice accreditation, CHAP survey, ACHC survey" />
        <link rel="canonical" href="https://noblehospicesolutions.com/hospice-surveys" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Hospice Survey Preparation Services",
            "provider": {
              "@type": "Organization",
              "name": "Noble Hospice Solutions",
              "url": "https://noblehospicesolutions.com"
            },
            "description": "Expert hospice survey preparation including mock surveys, staff training, and compliance assessments.",
            "areaServed": "United States"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-semibold mb-6">
              <Medal weight="fill" className="w-4 h-4" />
              Survey Success Experts
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6">
              Hospice Survey Preparation
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Achieve Zero Deficiencies
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Our proven survey preparation methodology has helped over 100 hospice agencies 
              achieve successful CMS surveys. Get ready with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Calendar weight="bold" className="w-5 h-5" />
                Free Readiness Assessment
              </Link>
              <a
                href="tel:+17183046214"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Phone weight="bold" className="w-5 h-5" />
                Call (718) 304-6214
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Survey Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-4">
              Key CMS Survey Focus Areas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              CMS surveyors evaluate your hospice across multiple dimensions. We ensure you're prepared for every area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {surveyAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-200"
              >
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4">{area.title}</h3>
                <ul className="space-y-2">
                  {area.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Deficiencies */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-6">
                Common Survey Deficiencies We Help You Avoid
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Don't let these common issues derail your survey. Our proactive approach identifies 
                and addresses potential deficiencies before surveyors arrive.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {commonDeficiencies.map((deficiency, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Warning weight="fill" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{deficiency}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl p-8 text-white">
              <h3 className="font-display text-2xl font-bold mb-6">Our Survey Prep Process</h3>
              <div className="space-y-4">
                {[
                  { step: "1", title: "Gap Analysis", desc: "Comprehensive review of current compliance status" },
                  { step: "2", title: "Action Planning", desc: "Prioritized roadmap to address deficiencies" },
                  { step: "3", title: "Staff Training", desc: "Role-specific education on survey expectations" },
                  { step: "4", title: "Mock Survey", desc: "Full simulation of actual survey experience" },
                  { step: "5", title: "Final Review", desc: "Last-minute check and confidence building" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-white/80 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-emerald-500 to-cyan-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
            Ready to Ace Your Next Hospice Survey?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join the 100+ hospice agencies that have achieved survey success with Noble Hospice Solutions.
          </p>
          <Link
            to="/schedule-demo"
            className="bg-white text-emerald-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
          >
            Get Your Free Assessment
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HospiceSurveysPage;
